import * as i1 from '@angular/common';
import { LocationStrategy, Location, APP_BASE_HREF, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Optional } from '@angular/core';
import { CHANNEL_TOKEN } from '@allianz/taly-core';

const WEB_COMPONENT_ID = new InjectionToken('WEB_COMPONENT_ID');

// this regex matches anything inside parenthesis
// example: using it on "my(secret)" will yield "secret"
const WEB_COMPONENT_ROUTE_REGEX = /\((.*?)\)/;
const WEB_COMPONENT_ROUTE_SEPARATOR = ',';
const WEB_COMPONENT_ROUTE_COMBINER = ':';
/**
 * @param hash the hash to extract the web component routing map from
 * @returns a map of web component ids and their associated paths
 */
function extractWebComponentRoutesFromHash(hash) {
    const webComponentRoutes = hash.match(WEB_COMPONENT_ROUTE_REGEX);
    if (!webComponentRoutes) {
        return {};
    }
    const routes = webComponentRoutes[1].split(WEB_COMPONENT_ROUTE_SEPARATOR);
    const webCompRoutes = routes.reduce((acc, route) => {
        const routeParts = route.split(WEB_COMPONENT_ROUTE_COMBINER);
        acc[routeParts[0]] = routeParts[1];
        return acc;
    }, {});
    return webCompRoutes;
}
/**
 * @param routes key:value map of web component routes
 * @returns string with web component routes encoded for usage in a URL
 */
function urlEncodeWebComponentRoutes(routes) {
    const entries = Object.entries(routes);
    if (entries.length === 0) {
        return '';
    }
    const routeStrings = entries.map(([id, path]) => `${id}:${path}`);
    return `(${routeStrings.join(WEB_COMPONENT_ROUTE_SEPARATOR)})`;
}
/**
 * @param webCompId ID of the web component
 * @param path the internal path of a web component
 * @param hash hash in which to update/insert this new path for the given web component id
 * @returns the given hash including the new/changed web component path
 */
function upsertWebComponentRoutesInHash(webCompId, path, hash) {
    const existingRoutes = extractWebComponentRoutesFromHash(hash);
    const combinedRoutes = Object.assign(Object.assign({}, existingRoutes), { [webCompId]: path });
    const routesString = urlEncodeWebComponentRoutes(combinedRoutes);
    const existingWebComponentRoutes = hash.match(WEB_COMPONENT_ROUTE_REGEX);
    if (!existingWebComponentRoutes) {
        return hash + routesString;
    }
    return hash.replace(existingWebComponentRoutes[0], routesString);
}

class WebComponentLocationStrategy extends LocationStrategy {
    constructor(_platformLocation, webComponentName, href) {
        super();
        this._platformLocation = _platformLocation;
        this.webComponentName = webComponentName;
        this._removeListenerFns = [];
        if (!href) {
            href = this._platformLocation.getBaseHrefFromDOM();
        }
        if (!href) {
            // No APP_BASE_HREF given and no <base> element present in
            // this document. As this will be part of a web component we don't
            // really care since we only rely on the DEPLOY_URL.
            // Using empty string as a fallback.
            href = '';
        }
        this._baseHref = href;
    }
    ngOnDestroy() {
        this._removeListenerFns.forEach((removeListenerFn) => removeListenerFn());
        this._removeListenerFns.length = 0;
    }
    getBaseHref() {
        return this._baseHref;
    }
    path() {
        var _a;
        const hash = this._platformLocation.hash;
        const routes = extractWebComponentRoutesFromHash(hash);
        return (_a = routes[this.webComponentId]) !== null && _a !== void 0 ? _a : '';
    }
    prepareExternalUrl(url) {
        var _a;
        const path = this._platformLocation.pathname;
        const hash = (_a = this._platformLocation.hash) !== null && _a !== void 0 ? _a : '';
        const updatedHash = upsertWebComponentRoutesInHash(this.webComponentId, url, hash);
        const prefixedHash = updatedHash.startsWith('#') ? updatedHash : '#' + updatedHash;
        return Location.joinWithSlash(path, prefixedHash);
    }
    pushState(state, title, url) {
        const hash = this.prepareExternalUrl(url);
        this._platformLocation.pushState(state, title, hash);
    }
    replaceState(state, title, url) {
        const hash = this.prepareExternalUrl(url);
        this._platformLocation.replaceState(state, title, hash);
    }
    onPopState(fn) {
        this._removeListenerFns.push(this._platformLocation.onPopState(fn), this._platformLocation.onHashChange(fn));
    }
    forward() {
        this._platformLocation.forward();
    }
    back() {
        this._platformLocation.back();
    }
    historyGo(relativePosition = 0) {
        var _a, _b;
        (_b = (_a = this._platformLocation).historyGo) === null || _b === void 0 ? void 0 : _b.call(_a, relativePosition);
    }
    // turns any whitespace in the web component name into underscores
    get webComponentId() {
        return this.webComponentName.replace(/ /g, '_');
    }
}
WebComponentLocationStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: WebComponentLocationStrategy, deps: [{ token: i1.PlatformLocation }, { token: WEB_COMPONENT_ID }, { token: APP_BASE_HREF, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
WebComponentLocationStrategy.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: WebComponentLocationStrategy });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: WebComponentLocationStrategy, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.PlatformLocation }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [WEB_COMPONENT_ID]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [APP_BASE_HREF]
                    }] }];
    } });

/* eslint-disable @typescript-eslint/no-empty-function */
class NoopLocationStrategy extends LocationStrategy {
    constructor(_platformLocation, href) {
        super();
        this._platformLocation = _platformLocation;
        if (!href) {
            href = this._platformLocation.getBaseHrefFromDOM();
        }
        if (!href) {
            // No APP_BASE_HREF given and no <base> element present in
            // this document. As this will be part of a web component we don't
            // really care since we only rely on the DEPLOY_URL.
            // Using empty string as a fallback.
            href = '';
        }
        this._baseHref = href;
    }
    getBaseHref() {
        return this._baseHref;
    }
    path() {
        return '';
    }
    prepareExternalUrl() {
        return this._platformLocation.href;
    }
    pushState() { }
    replaceState() { }
    onPopState() { }
    forward() { }
    back() { }
}
NoopLocationStrategy.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: NoopLocationStrategy, deps: [{ token: i1.PlatformLocation }, { token: APP_BASE_HREF, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
NoopLocationStrategy.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: NoopLocationStrategy });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: NoopLocationStrategy, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1.PlatformLocation }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: [APP_BASE_HREF]
                    }] }];
    } });

/**
 * Abstract service to integrate a stage back link adapter into an application.
 *
 * Requires an actual implementation like the `StageBackLinkAemAdapterService`.
 * Provide it in your app root module.
 *
 * {
 *   provide: StageBackLinkAdapterService,
 *   useClass: StageBackLinkAemAdapterService
 * }
 *
 * @deprecated Replace with `BackLinkAdapterService`. Read this migration guide for more information: https://gdf-taly-workspace.azureedge.net/additional-documentation/app-generation/web-component-utilities.html#34-migration-from-stagebacklinkadapterservice-to-backlinkadapterservice-provider
 */
class StageBackLinkAdapterService {
}

/**
 * @deprecated Replace with `BackLinkAemAdapterService`. Read this migration guide for more information: https://gdf-taly-workspace.azureedge.net/additional-documentation/app-generation/web-component-utilities.html#34-migration-from-stagebacklinkadapterservice-to-backlinkadapterservice-provider
 */
class StageBackLinkAemAdapterService extends StageBackLinkAdapterService {
    constructor(webComponentId, document) {
        super();
        this.webComponentId = webComponentId;
        this.document = document;
        this.defaultLinkLabel = $localize `:@@frame.stage.default-back-link-label:Back to dashboard`;
        this.rootElemAttribute = 'customconfiguration';
        this.stageBackLinkData = this.extractStageBackLinkData();
    }
    extractStageBackLinkConfig() {
        let config;
        let parsedStageBackLinkConfig;
        const rootElem = this.document.querySelector(this.webComponentId);
        if (!rootElem)
            return;
        const configString = rootElem.getAttribute(this.rootElemAttribute);
        if (!configString)
            return;
        try {
            config = JSON.parse(configString);
            if (!config)
                return;
        }
        catch (error) {
            throw Error(`Error while parsing the content from the "${this.rootElemAttribute}" attribute in the "${this.webComponentId}" element\n${error}`);
        }
        try {
            const stageBackLinkConfig = config.stageBackLinkConfig;
            if (!stageBackLinkConfig)
                return;
            parsedStageBackLinkConfig = JSON.parse(stageBackLinkConfig);
        }
        catch (error) {
            throw Error(`Error while parsing the content from the "stageBackLinkConfig" field in "${this.rootElemAttribute}". The stage back link configuration doesn't have the proper format\n${error}`);
        }
        return parsedStageBackLinkConfig;
    }
    getDefaultData() {
        let backLinkData;
        const config = this.stageBackLinkConfig;
        if ((config === null || config === void 0 ? void 0 : config.default) && (config === null || config === void 0 ? void 0 : config.default.path)) {
            backLinkData = {
                path: config.default.path,
                label: config.default.label || this.defaultLinkLabel
            };
        }
        return backLinkData;
    }
    extractStageBackLinkData() {
        var _a, _b;
        this.stageBackLinkConfig = this.extractStageBackLinkConfig();
        if (!this.stageBackLinkConfig)
            return;
        const referrer = this.document.referrer;
        if (!referrer) {
            return this.getDefaultData();
        }
        const configItem = (_a = this.stageBackLinkConfig.backLinks) === null || _a === void 0 ? void 0 : _a.find((backLink) => referrer.includes(backLink.path));
        if (!configItem) {
            return this.getDefaultData();
        }
        const linkLabel = (_b = configItem.label) !== null && _b !== void 0 ? _b : this.defaultLinkLabel;
        const backLinkData = {
            path: this.document.referrer,
            label: linkLabel
        };
        return backLinkData;
    }
}
StageBackLinkAemAdapterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: StageBackLinkAemAdapterService, deps: [{ token: WEB_COMPONENT_ID }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
StageBackLinkAemAdapterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: StageBackLinkAemAdapterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: StageBackLinkAemAdapterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [WEB_COMPONENT_ID]
                    }] }, { type: Document, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }];
    } });

/**
 * Abstract service to integrate a back link adapter into an application.
 *
 * Requires an actual implementation like the `BackLinkAemAdapterService`.
 * Provide it in your app root module.
 *
 * {
 *   provide: BackLinkAdapterService,
 *   useClass: BackLinkAemAdapterService
 * }
 */
class BackLinkAdapterService {
}

class BackLinkAemAdapterService extends BackLinkAdapterService {
    constructor(webComponentId, document) {
        super();
        this.webComponentId = webComponentId;
        this.document = document;
        this.rootElemAttribute = 'customconfiguration';
        this.backLinkData = this.extractBackLinkData();
    }
    extractBackLinkConfig() {
        let config;
        let parsedBackLinkConfig;
        const rootElem = this.document.querySelector(this.webComponentId);
        if (!rootElem)
            return;
        const configString = rootElem.getAttribute(this.rootElemAttribute);
        if (!configString)
            return;
        try {
            config = JSON.parse(configString);
            if (!config)
                return;
        }
        catch (error) {
            throw Error(`Error while parsing the content from the "${this.rootElemAttribute}" attribute in the "${this.webComponentId}" element\n${error}`);
        }
        try {
            const backLinkConfig = config.backLinkConfig;
            if (!backLinkConfig)
                return;
            parsedBackLinkConfig = JSON.parse(backLinkConfig);
        }
        catch (error) {
            throw Error(`Error while parsing the content from the "backLinkConfig" field in "${this.rootElemAttribute}". The back link configuration doesn't have the proper format\n${error}`);
        }
        return parsedBackLinkConfig;
    }
    getDefaultData() {
        var _a, _b;
        let backLinkData;
        if ((_b = (_a = this.backLinkConfig) === null || _a === void 0 ? void 0 : _a.default) === null || _b === void 0 ? void 0 : _b.path) {
            backLinkData = this.backLinkConfig.default;
        }
        return backLinkData;
    }
    extractBackLinkData() {
        var _a;
        this.backLinkConfig = this.extractBackLinkConfig();
        if (!this.backLinkConfig)
            return;
        const referrer = this.document.referrer;
        if (!referrer) {
            return this.getDefaultData();
        }
        const configItem = (_a = this.backLinkConfig.backLinks) === null || _a === void 0 ? void 0 : _a.find((backLink) => referrer.includes(backLink.path));
        if (!configItem) {
            return this.getDefaultData();
        }
        const backLinkData = {
            path: this.document.referrer,
            stageLinkLabel: configItem.stageLinkLabel,
            nextButtonLabel: configItem.nextButtonLabel
        };
        return backLinkData;
    }
}
BackLinkAemAdapterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BackLinkAemAdapterService, deps: [{ token: WEB_COMPONENT_ID }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
BackLinkAemAdapterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BackLinkAemAdapterService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BackLinkAemAdapterService, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [WEB_COMPONENT_ID]
                    }] }, { type: Document, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }];
    } });

class BackLinkUtilsService {
    constructor(channel, backLinkAdapterService) {
        this.backLinkAdapterService = backLinkAdapterService;
        this.isRetailChannel = channel === "retail" /* RETAIL */;
    }
    isBackLinkFeatureActiveForStage(hideBackLink) {
        var _a, _b;
        return Boolean(this.isRetailChannel &&
            !hideBackLink &&
            ((_b = (_a = this.backLinkAdapterService) === null || _a === void 0 ? void 0 : _a.backLinkData) === null || _b === void 0 ? void 0 : _b.stageLinkLabel));
    }
    isBackLinkFeatureActiveForActions(isLastPage) {
        var _a, _b;
        return Boolean(this.isRetailChannel &&
            isLastPage &&
            ((_b = (_a = this.backLinkAdapterService) === null || _a === void 0 ? void 0 : _a.backLinkData) === null || _b === void 0 ? void 0 : _b.nextButtonLabel));
    }
}
BackLinkUtilsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BackLinkUtilsService, deps: [{ token: CHANNEL_TOKEN }, { token: BackLinkAdapterService, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
BackLinkUtilsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BackLinkUtilsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.9", ngImport: i0, type: BackLinkUtilsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: [CHANNEL_TOKEN]
                    }] }, { type: BackLinkAdapterService, decorators: [{
                        type: Optional
                    }] }];
    } });

/**
 * Generated bundle index. Do not edit.
 */

export { BackLinkAdapterService, BackLinkAemAdapterService, BackLinkUtilsService, NoopLocationStrategy, StageBackLinkAdapterService, StageBackLinkAemAdapterService, WEB_COMPONENT_ID, WebComponentLocationStrategy };
